import React from "react";
import { Box, Grid, Paper, Typography, Button } from "@mui/material";
import { BooleanField, DateField, Labeled, RichTextField, Show, TextField, useNotify, useRecordContext, useRefresh } from "react-admin";
import axios from "axios";
import { ContentPageSX } from "../../../styled/custom";
import LinkField from "../../../ui/link-field";
import ControlAudio from "../../../ui/control-audio";
import ControlVideo from "../../../ui/control-video";
import { MediaThumbnailField } from "../../../ui/media-thumbnail";
import ProcessingStatusField from "../../../ui/processing-status-field";
import config from "../../../config/config";
import { headers } from "../../../provider/data-provider";

const sectionSx = {
  p: 2,
  borderRadius: "10px",
  border: "1px solid rgba(0,0,0,0.09)",
  backgroundColor: "#fff",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
  height: "100%",
};

const sectionTitleSx = {
  fontSize: "0.95rem",
  fontWeight: 700,
  color: "#2f2f2f",
  mb: 1.2,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

const textBodyBoxSx = {
  background: "#fefefe",
  border: "1px solid rgba(0,0,0,0.15)",
  padding: "10px",
  borderRadius: "8px",
  minHeight: "220px",
  overflow: "auto",
};

const videoContentBoxSx = {
  width: "100%",
  maxWidth: "760px",
  maxHeight: "430px",
  border: "1px solid rgba(0,0,0,0.15)",
  borderRadius: "8px",
  overflow: "hidden",
  backgroundColor: "#000",
  "& video": {
    display: "block",
    width: "100%",
    height: "auto",
    maxHeight: "430px",
  },
};

const audioContentBoxSx = {
  width: "100%",
  border: "1px solid rgba(0,0,0,0.15)",
  borderRadius: "8px",
  padding: "10px",
  backgroundColor: "#fefefe",
  overflow: "hidden",
};

const MediaContentField = () => {
  const record = useRecordContext();

  if (!record) return null;

  if (record.media_class === "Video" && record.url) {
    return (
      <Labeled label="Player de Video" fullWidth>
        <Box sx={videoContentBoxSx}>
          <ControlVideo />
        </Box>
      </Labeled>
    );
  }

  if (record.media_class === "Audio" && record.url) {
    return (
      <Labeled label="Player de Audio" fullWidth>
        <Box sx={audioContentBoxSx}>
          <ControlAudio />
        </Box>
      </Labeled>
    );
  }

  return (
    <Labeled label="Corpo" fullWidth>
      <Box sx={textBodyBoxSx}>
        <RichTextField source="body" label="Corpo" sortable={false} />
      </Box>
    </Labeled>
  );
};

const MediaProcessingStatusField = () => {
  const record = useRecordContext();
  return <ProcessingStatusField status={record?.processing_status} size="large" />;
};

const RetryPreviewButton = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const status = String(record?.processing_status || "").toUpperCase().trim();
  const canRetry = Boolean(record?.id) && status !== "OK" && status !== "PROCESSING";

  if (!canRetry) return null;

  const onRetry = async () => {
    try {
      setIsSubmitting(true);
      await axios.post(`${config.api.url}/media/${record.id}/reprocess-preview`, {}, { headers: headers() });
      notify("Reprocessamento de preview iniciado com sucesso.", { type: "success" });
      refresh();
    } catch (error) {
      const message =
        error?.response?.data?.error?.processing_status ||
        error?.response?.data?.error?.media ||
        "Não foi possível iniciar o reprocessamento do preview.";
      notify(message, { type: "warning" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Button variant="contained" onClick={onRetry} disabled={isSubmitting}>
      {isSubmitting ? "Iniciando..." : "Regerar Preview"}
    </Button>
  );
};

const MediaShow = () => (
  <Show title="Produto da Cesta" sx={ContentPageSX}>
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mx: "auto", width: "100%" }}>
      <Paper sx={sectionSx}>
        <Typography sx={sectionTitleSx}>Resumo</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={2}>
            <Labeled label="Id">
              <TextField source="id" />
            </Labeled>
          </Grid>
          <Grid item xs={12} md={3}>
            <Labeled label="Classe">
              <TextField source="media_class" sortable={false} />
            </Labeled>
          </Grid>
          <Grid item xs={12} md={4}>
            <Labeled label="Categoria">
              <TextField source="category_media.name" sortable={false} />
            </Labeled>
          </Grid>
          <Grid item xs={12} md={3}>
            <Labeled label="Criado em">
              <DateField source="created_at" locales="pt-BR" sortable={false} showTime />
            </Labeled>
          </Grid>
          <Grid item xs={12} md={3}>
            <Labeled label="Expira em">
              <DateField source="expires_at" locales="pt-BR" sortable={false} showTime />
            </Labeled>
          </Grid>
          <Grid item xs={12} md={2}>
            <Labeled label="Expirado">
              <BooleanField source="is_expired" />
            </Labeled>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Paper sx={sectionSx}>
            <Typography sx={sectionTitleSx}>Conteúdo</Typography>
            <TextField source="name" label="Nome" sortable={false} />
            <TextField source="description" label="Descrição" sortable={false} />
            <MediaContentField />
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={sectionSx}>
            <Typography sx={sectionTitleSx}>Thumbnail</Typography>
            <MediaThumbnailField scale={0.55} />
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={sectionSx}>
        <Typography sx={sectionTitleSx}>Arquivo e Publicação</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <Labeled label="Downloads">
              <TextField source="number_downloads" sortable={false} />
            </Labeled>
          </Grid>
          <Grid item xs={12} md={3}>
            <Labeled label="Tipo">
              <TextField source="type" sortable={false} />
            </Labeled>
          </Grid>
          <Grid item xs={12} md={3}>
            <Labeled label="Status do processamento (geração de preview)">
              <MediaProcessingStatusField />
            </Labeled>
          </Grid>
          <Grid item xs={12} md={3}>
            <Labeled label="Url">
              <LinkField source="url" text="Ver" sortable={false} />
            </Labeled>
          </Grid>
          <Grid item xs={12}>
            <RetryPreviewButton />
          </Grid>
        </Grid>
      </Paper>
    </Box>
  </Show>
);

export default MediaShow;
